import React, { useEffect, useRef, useState } from 'react';
import { THUMB_MODES, useSettings } from '../settings/SettingsContext';

/**
 * Display settings, tucked behind a sliders icon at the end of the nav.
 *
 * These belong out of the content flow: sat next to the feed's chips and
 * search box they read as another filter, which is the one thing they are not.
 * A nav popover keeps them quiet but still one click away, so a change can be
 * judged against the list it just changed rather than from a settings page
 * with nothing on it.
 */
export default function SettingsMenu() {
  const { thumbs, setThumbs } = useSettings();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // mousedown rather than click: closing on the press means a click that
    // lands outside dismisses the panel without also activating whatever it
    // landed on feeling like a double action.
    function onPointerDown(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="settings-menu" ref={wrap}>
      {/* Sliders rather than a cog: a cog in a nav reads as account settings,
          and none of this touches the account. */}
      <button
        type="button"
        className="settings-trigger"
        onClick={() => setOpen(o => !o)}
        aria-label="Display settings"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" />
        </svg>
      </button>

      {open && (
        <div className="settings-panel">
          <div className="settings-row">
            <span className="settings-label">
              Images
              <span className="settings-hint">News thumbnails across the site</span>
            </span>
            {/* A segmented control, not three loose chips: the options are
                mutually exclusive and this is a settings panel, not a filter
                row that happens to be somewhere else. */}
            <span className="settings-choices" role="group" aria-label="Thumbnail images">
              {THUMB_MODES.map(m => (
                <button
                  key={m.key}
                  type="button"
                  className={`settings-choice${
                    thumbs === m.key ? ' settings-choice-active' : ''
                  }`}
                  onClick={() => setThumbs(m.key)}
                  aria-pressed={thumbs === m.key}
                >
                  {m.label}
                </button>
              ))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
